import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FaPlus, FaTrash } from "react-icons/fa";
import {
  createPurchaseOrder, updatePurchaseOrder, getPurchaseOrder, searchApprovedPurchaseRequests,
} from "redux/erp/purchaseOrderSlice";
import SlideDrawer from "../purchase/common/SlideDrawer";
import FormSection from "../purchase/common/FormSection";
import Clickable from "../purchase/common/Clickable";
import { GeistInput, GeistSelect } from "../purchase/common/GeistField";
import StepperBar from "../purchase/common/Stepper";

const STEPS = [
  { key: "supplier", label: "Requisition & supplier" },
  { key: "terms", label: "Order details & terms" },
  { key: "items", label: "Item lines" },
];

const TH = "tw-px-2 tw-py-2 tw-text-left tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-geist-fg-3 tw-whitespace-nowrap";
const cellInput = "tw-w-full tw-px-2 tw-py-1.5 tw-text-[13px] tw-bg-transparent tw-border-0 tw-border-b tw-border-solid tw-border-geist-border-2 hover:tw-border-geist-fg-3 focus:tw-border-geist-accent focus:tw-outline-none tw-transition-colors";

const emptyItem = () => ({ _key: Math.random().toString(36).slice(2), description: "", unit: "", qty: "", unit_price: "", amount: "" });

// Purchaser tự fill = tên user đang đăng nhập (người lập PO) — đỡ phải gõ lại.
const blankForm = (profile = {}) => ({
  purchase_request_id: "",
  supplier_name: "", supplier_attn: "", supplier_phone: "", supplier_fax: "", supplier_title: "",
  po_date: new Date().toISOString().slice(0, 10),
  item_ref: "", ref_no: "", cost_center: "", job: "",
  purchaser_name: profile.full_name || profile.name || "",
  payment_term: "", delivery_to: "", delivery_time: "", enclosed_doc: "", certificates_requested: "",
  tax_rate: 0,
  items: [emptyItem()],
});

// PO chỉ được tạo từ 1 Purchase Requisition đã approved.
// presetPurchaseRequest (khi mở từ trang PR detail) khoá sẵn purchase_request_id + gợi ý vendor.
const PurchaseOrderDrawer = ({ show, onHide, purchaseOrder, presetPurchaseRequest, onSaved }) => {
  const dispatch = useDispatch();
  const profile = useSelector((s) => s.profile_user?.data || {});
  const [form, setForm] = useState(blankForm(profile));
  const [prOptions, setPrOptions] = useState([]);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const isEdit = !!purchaseOrder?.id;

  useEffect(() => {
    if (!show || presetPurchaseRequest || isEdit) return;
    searchApprovedPurchaseRequests().then((res) => setPrOptions(res?.data || [])).catch(() => setPrOptions([]));
  }, [show, presetPurchaseRequest, isEdit]);

  useEffect(() => {
    if (!show) return;
    setStep(0);
    if (purchaseOrder?.id) {
      setForm({
        purchase_request_id: purchaseOrder.purchase_request_id || "",
        supplier_name: purchaseOrder.supplier_name || "",
        supplier_attn: purchaseOrder.supplier_attn || "",
        supplier_phone: purchaseOrder.supplier_phone || "",
        supplier_fax: purchaseOrder.supplier_fax || "",
        supplier_title: purchaseOrder.supplier_title || "",
        po_date: purchaseOrder.po_date || new Date().toISOString().slice(0, 10),
        item_ref: purchaseOrder.item_ref || "",
        ref_no: purchaseOrder.ref_no || "",
        cost_center: purchaseOrder.cost_center || "",
        job: purchaseOrder.job || "",
        purchaser_name: purchaseOrder.purchaser_name || "",
        payment_term: purchaseOrder.payment_term || "",
        delivery_to: purchaseOrder.delivery_to || "",
        delivery_time: purchaseOrder.delivery_time || "",
        enclosed_doc: purchaseOrder.enclosed_doc || "",
        certificates_requested: purchaseOrder.certificates_requested || "",
        tax_rate: purchaseOrder.tax_rate || 0,
        items: (purchaseOrder.items || []).map((i) => ({ _key: `db-${i.id}`, ...i })).length
          ? (purchaseOrder.items || []).map((i) => ({ _key: `db-${i.id}`, ...i }))
          : [emptyItem()],
      });
    } else {
      const f = blankForm(profile);
      if (presetPurchaseRequest) {
        f.purchase_request_id = presetPurchaseRequest.id;
        f.supplier_name = presetPurchaseRequest.vendor_name || "";
        f.supplier_attn = presetPurchaseRequest.vendor_attn || "";
        f.supplier_phone = presetPurchaseRequest.vendor_phone || "";
        f.supplier_fax = presetPurchaseRequest.vendor_fax || "";
        f.supplier_title = presetPurchaseRequest.vendor_title || "";
        f.delivery_to = presetPurchaseRequest.delivery_to || "";
        f.items = (presetPurchaseRequest.items || []).length
          ? presetPurchaseRequest.items.map((i) => ({
              _key: `pr-${i.id}`, description: i.description, unit: i.unit, qty: i.qty, unit_price: i.unit_price || 0, amount: i.amount || 0,
            }))
          : [emptyItem()];
      }
      setForm(f);
    }
    // Seed only when the drawer opens or its source record changes: `profile` is a fresh
    // `{}` fallback on every render and would wipe what the user is typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, purchaseOrder, presetPurchaseRequest]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setItem = (key, patch) => setForm((f) => ({
    ...f,
    items: f.items.map((it) => {
      if (it._key !== key) return it;
      const next = { ...it, ...patch };
      next.amount = (Number(next.qty) || 0) * (Number(next.unit_price) || 0);
      return next;
    }),
  }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }));
  const removeItem = (key) => setForm((f) => ({ ...f, items: f.items.length > 1 ? f.items.filter((it) => it._key !== key) : f.items }));

  const subtotal = form.items.reduce((s, i) => s + (Number(i.amount) || 0), 0);
  const taxAmount = subtotal * (Number(form.tax_rate) || 0) / 100;
  const total = subtotal + taxAmount;

  // Validate từng bước trước khi cho "Continue".
  const validateStep = (s) => {
    if (s === 0) {
      if (!form.purchase_request_id) { toast.warning("Please select an approved Purchase Requisition."); return false; }
      if (!form.supplier_name.trim()) { toast.warning("Please enter Supplier name."); return false; }
    }
    return true;
  };
  const goNext = () => { if (validateStep(step)) setStep((s) => Math.min(s + 1, STEPS.length - 1)); };
  const goBack = () => setStep((s) => Math.max(s - 1, 0));
  const jumpTo = (idx) => { if (idx <= step || validateStep(step)) setStep(idx); };

  const onSubmit = async () => {
    if (!validateStep(0)) { setStep(0); return; }
    if (!form.items.some((i) => i.description?.trim())) { toast.warning("Please add at least 1 item line."); return; }

    setBusy(true);
    try {
      const data = {
        purchase_request_id: form.purchase_request_id,
        supplier_name: form.supplier_name, supplier_attn: form.supplier_attn, supplier_phone: form.supplier_phone,
        supplier_fax: form.supplier_fax, supplier_title: form.supplier_title,
        po_date: form.po_date, item_ref: form.item_ref, ref_no: form.ref_no, cost_center: form.cost_center,
        job: form.job, purchaser_name: form.purchaser_name,
        payment_term: form.payment_term, delivery_to: form.delivery_to, delivery_time: form.delivery_time,
        enclosed_doc: form.enclosed_doc, certificates_requested: form.certificates_requested, tax_rate: form.tax_rate,
        items_attributes: form.items.filter((i) => i.description?.trim()).map((i) => ({
          id: typeof i.id === "number" ? i.id : undefined,
          description: i.description, unit: i.unit, qty: i.qty || 0, unit_price: i.unit_price || 0, amount: i.amount || 0,
        })),
      };

      let newId;
      if (isEdit) {
        await dispatch(updatePurchaseOrder({ id: purchaseOrder.id, data })).unwrap();
        toast.success("Purchase order updated.");
        dispatch(getPurchaseOrder(purchaseOrder.id));
      } else {
        const res = await dispatch(createPurchaseOrder(data)).unwrap();
        newId = res?.data?.id;
        toast.success("Purchase order created.");
      }
      onSaved?.(newId);
      onHide();
    } catch (e) {
      toast.error(e?.error || e?.message || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const title = (
    <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
      <span className="tw-inline-flex tw-h-8 tw-w-8 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-geist-sm tw-bg-geist-100 tw-text-geist-accent">
        <i className="bi bi-file-earmark-plus" />
      </span>
      <h2 className="tw-text-[15px] tw-font-semibold tw-text-geist-fg tw-m-0 tw-truncate">
        {isEdit ? `Edit ${purchaseOrder?.code}` : "New Purchase Order"}
      </h2>
      <span className="tw-text-xs tw-font-normal tw-text-geist-fg-3 tw-flex-shrink-0">· QF-PUR-03</span>
    </div>
  );

  const isLastStep = step === STEPS.length - 1;
  const footer = (
    <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
      {step > 0 ? (
        <Clickable onClick={goBack} disabled={busy}
          className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-3 tw-rounded-geist-sm tw-text-geist-fg-2 tw-text-[13px] tw-font-medium hover:tw-bg-geist-100">
          <i className="bi bi-arrow-left" /> Back
        </Clickable>
      ) : <span />}
      <div className="tw-flex tw-items-center tw-gap-2">
        <Clickable onClick={onHide} disabled={busy}
          className="tw-inline-flex tw-items-center tw-h-9 tw-px-4 tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-text-geist-fg-2 tw-text-[13px] tw-font-medium hover:tw-bg-geist-100">
          Cancel
        </Clickable>
        {isLastStep ? (
          <Clickable onClick={onSubmit} disabled={busy}
            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-4 tw-rounded-geist-sm tw-bg-geist-accent tw-text-white tw-text-[13px] tw-font-medium hover:tw-bg-geist-accent-2 disabled:tw-opacity-60">
            {busy && <i className="bi bi-arrow-repeat tw-animate-spin" />}
            {busy ? "Saving..." : "Save"}
          </Clickable>
        ) : (
          <Clickable onClick={goNext} disabled={busy}
            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-4 tw-rounded-geist-sm tw-bg-geist-accent tw-text-white tw-text-[13px] tw-font-medium hover:tw-bg-geist-accent-2">
            Continue <i className="bi bi-arrow-right" />
          </Clickable>
        )}
      </div>
    </div>
  );

  return (
    <SlideDrawer show={show} onHide={onHide} title={title} footer={footer} width="72%" minWidth="820px" bodyClassName="tw-bg-geist-bg-2">
      <div className="geist-scope tw-space-y-4">
        <StepperBar steps={STEPS} step={step} onJump={jumpTo} />

        {step === 0 && (
          <>
            <FormSection icon="bi-link-45deg" title="Purchase Requisition (approved)">
              {presetPurchaseRequest ? (
                <div className="tw-h-10 tw-flex tw-items-center tw-gap-2 tw-px-3 tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-bg-geist-bg-2 tw-text-sm tw-text-geist-fg">
                  <i className="bi bi-check-circle-fill tw-text-geist-success" /> {presetPurchaseRequest.code} · {presetPurchaseRequest.vendor_name}
                </div>
              ) : isEdit ? (
                <div className="tw-h-10 tw-flex tw-items-center tw-px-3 tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-bg-geist-bg-2 tw-text-sm tw-text-geist-fg">
                  {purchaseOrder?.purchase_request?.code}
                </div>
              ) : (
                <GeistSelect
                  required
                  value={form.purchase_request_id}
                  onChange={(e) => setField("purchase_request_id", e.target.value)}
                  placeholder="Select an approved requisition..."
                  options={prOptions}
                />
              )}
            </FormSection>

            <FormSection icon="bi-truck" title="Supplier">
              <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-x-6 tw-gap-y-4">
                <GeistInput label="To (Supplier name)" required value={form.supplier_name} onChange={(e) => setField("supplier_name", e.target.value)} />
                <GeistInput label="Attn." value={form.supplier_attn} onChange={(e) => setField("supplier_attn", e.target.value)} />
                <GeistInput label="Title" value={form.supplier_title} onChange={(e) => setField("supplier_title", e.target.value)} />
                <GeistInput label="Phone" value={form.supplier_phone} onChange={(e) => setField("supplier_phone", e.target.value)} />
                <GeistInput label="Fax" value={form.supplier_fax} onChange={(e) => setField("supplier_fax", e.target.value)} />
              </div>
            </FormSection>
          </>
        )}

        {step === 1 && (
          <>
            <FormSection icon="bi-clipboard-data" title="Order details">
              <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-x-6 tw-gap-y-4">
                <GeistInput label="PO date" type="date" value={form.po_date} onChange={(e) => setField("po_date", e.target.value)} />
                <GeistInput label="Item / Hạng mục" value={form.item_ref} onChange={(e) => setField("item_ref", e.target.value)} />
                <GeistInput label="Ref." value={form.ref_no} onChange={(e) => setField("ref_no", e.target.value)} />
                <GeistInput label="Cost center" value={form.cost_center} onChange={(e) => setField("cost_center", e.target.value)} />
                <GeistInput label="Job" value={form.job} onChange={(e) => setField("job", e.target.value)} />
                <GeistInput label="Purchaser" value={form.purchaser_name} onChange={(e) => setField("purchaser_name", e.target.value)} />
              </div>
            </FormSection>

            <FormSection icon="bi-geo-alt" title="Terms & delivery">
              <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-x-6 tw-gap-y-4">
                <GeistInput label="Payment term" type="textarea" rows={2} value={form.payment_term} onChange={(e) => setField("payment_term", e.target.value)} />
                <GeistInput label="Delivery to" value={form.delivery_to} onChange={(e) => setField("delivery_to", e.target.value)} />
                <GeistInput label="Delivery time" value={form.delivery_time} onChange={(e) => setField("delivery_time", e.target.value)} />
                <GeistInput label="Enclosed doc." value={form.enclosed_doc} onChange={(e) => setField("enclosed_doc", e.target.value)} />
                <GeistInput label="Certificates requested" value={form.certificates_requested} onChange={(e) => setField("certificates_requested", e.target.value)} />
                <GeistInput label="Tax rate (%)" type="number" value={form.tax_rate} onChange={(e) => setField("tax_rate", e.target.value)} />
              </div>
            </FormSection>
          </>
        )}

        {step === 2 && (
          <FormSection icon="bi-list-ul" title="Item lines" bodyClassName="tw-p-3 sm:tw-p-4"
            right={
              <Clickable onClick={addItem}
                className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-semibold tw-text-geist-accent hover:tw-underline">
                <FaPlus size={9} /> Add line
              </Clickable>
            }>
            <div className="tw-overflow-x-auto">
              <table className="tw-w-full tw-border-collapse">
                <thead>
                  <tr className="tw-border-0 tw-border-b tw-border-solid tw-border-geist-border">
                    <th className={`${TH} tw-min-w-[200px]`}>Description</th>
                    <th className={`${TH} tw-min-w-[70px]`}>Unit</th>
                    <th className={`${TH} tw-text-right`}>Qty</th>
                    <th className={`${TH} tw-text-right`}>Unit price</th>
                    <th className={`${TH} tw-text-right`}>Amount</th>
                    <th className={`${TH} tw-w-8`} />
                  </tr>
                </thead>
                <tbody>
                  {form.items.map((it, idx) => (
                    <tr key={it._key} className={idx % 2 === 1 ? "tw-bg-geist-bg-2" : "tw-bg-white"}>
                      <td className="tw-px-2 tw-py-1.5"><input className={cellInput} value={it.description || ""} onChange={(e) => setItem(it._key, { description: e.target.value })} /></td>
                      <td className="tw-px-2 tw-py-1.5"><input className={cellInput} value={it.unit || ""} onChange={(e) => setItem(it._key, { unit: e.target.value })} /></td>
                      <td className="tw-px-2 tw-py-1.5"><input type="number" className={`${cellInput} tw-text-right`} value={it.qty || ""} onChange={(e) => setItem(it._key, { qty: e.target.value })} /></td>
                      <td className="tw-px-2 tw-py-1.5"><input type="number" className={`${cellInput} tw-text-right`} value={it.unit_price || ""} onChange={(e) => setItem(it._key, { unit_price: e.target.value })} /></td>
                      <td className="tw-px-2 tw-py-1.5 tw-text-right tw-text-[13px] tw-tabular-nums tw-text-geist-fg-2">{(Number(it.amount) || 0).toLocaleString("en-US")}</td>
                      <td className="tw-px-2 tw-py-1.5 tw-text-center">
                        <Clickable onClick={() => removeItem(it._key)} className="tw-inline-flex tw-text-geist-fg-3 hover:tw-text-geist-error"><FaTrash size={11} /></Clickable>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="tw-border-t-2 tw-border-solid tw-border-geist-border tw-bg-geist-bg-2">
                    <td className="tw-px-2 tw-py-2.5 tw-text-right tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-geist-fg-2" colSpan={4}>Subtotal / Tax ({form.tax_rate || 0}%) / Total</td>
                    <td className="tw-px-2 tw-py-2.5 tw-text-right tw-text-sm tw-font-bold tw-text-geist-fg tw-tabular-nums">
                      {subtotal.toLocaleString("en-US")} + {taxAmount.toLocaleString("en-US")} = {total.toLocaleString("en-US")}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          </FormSection>
        )}
      </div>
    </SlideDrawer>
  );
};

const idType = PropTypes.oneOfType([PropTypes.number, PropTypes.string]);
const itemShape = PropTypes.shape({
  id: idType,
  description: PropTypes.string,
  unit: PropTypes.string,
  qty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  unit_price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
});

PurchaseOrderDrawer.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  // Present when editing; absent for a new PO.
  purchaseOrder: PropTypes.shape({
    id: idType,
    code: PropTypes.string,
    purchase_request_id: idType,
    purchase_request: PropTypes.shape({ code: PropTypes.string }),
    supplier_name: PropTypes.string,
    supplier_attn: PropTypes.string,
    supplier_phone: PropTypes.string,
    supplier_fax: PropTypes.string,
    supplier_title: PropTypes.string,
    po_date: PropTypes.string,
    item_ref: PropTypes.string,
    ref_no: PropTypes.string,
    cost_center: PropTypes.string,
    job: PropTypes.string,
    purchaser_name: PropTypes.string,
    payment_term: PropTypes.string,
    delivery_to: PropTypes.string,
    delivery_time: PropTypes.string,
    enclosed_doc: PropTypes.string,
    certificates_requested: PropTypes.string,
    tax_rate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    items: PropTypes.arrayOf(itemShape),
  }),
  // The approved Purchase Requisition the PO is opened from (locks the PR and suggests the vendor).
  presetPurchaseRequest: PropTypes.shape({
    id: idType,
    code: PropTypes.string,
    vendor_name: PropTypes.string,
    vendor_attn: PropTypes.string,
    vendor_phone: PropTypes.string,
    vendor_fax: PropTypes.string,
    vendor_title: PropTypes.string,
    delivery_to: PropTypes.string,
    items: PropTypes.arrayOf(itemShape),
  }),
  onSaved: PropTypes.func,
};

export default PurchaseOrderDrawer;
